import { useEffect, useMemo, useState } from 'react'
import { Button, HStack, Input, NativeSelect, Text, VStack, Field, Dialog, CloseButton } from '@chakra-ui/react';
import { ResponsiveModal } from '../../shared'
import {
  DAY_END_MINUTES, DAY_START_MINUTES, SLOT_MINUTES, constituencyFor, minutesToTime, timeToMinutes,
} from './lobbyScheduleUtils'
import type {
  LobbyDayAskDto, MeetingWith, ScheduleAssignmentDto, ScheduleAttendeeDto,
  ScheduleLegislatorRowDto, ScheduleMeetingDto,
} from '../../../types/lobbyDay'
import { uuid } from '../../../lib/uuid'

/** When `pick` is true the modal renders legislator + start-time selects (the "+ Meeting"
 *  header flow). Otherwise legislator + start time are fixed (drop-into-cell flow) and the
 *  provided legislatorId / slotTime are shown as text. */
export interface CreateMeetingContext {
  legislatorId: string
  slotTime: string // "HH:mm:ss"
  pendingAttendeeId?: string
  pick?: boolean
}

interface CreateMeetingPopoverProps {
  context: CreateMeetingContext | null
  asks: LobbyDayAskDto[]
  legislators: ScheduleLegislatorRowDto[]
  attendees: ScheduleAttendeeDto[]
  currentDay: string
  onCreate: (meeting: ScheduleMeetingDto) => void
  onClose: () => void
}

const WITH_OPTIONS: { value: MeetingWith; label: string }[] = [
  { value: 'Unknown', label: 'TBD' },
  { value: 'Legislator', label: 'Legislator' },
  { value: 'Staff', label: 'Staff' },
  { value: 'Both', label: 'Both' },
]

function formatSlotLabel(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

export function CreateMeetingPopover({
  context, asks, legislators, attendees, currentDay, onCreate, onClose,
}: CreateMeetingPopoverProps) {
  const slotValues = useMemo(() => {
    const values: string[] = []
    for (let t = DAY_START_MINUTES; t < DAY_END_MINUTES; t += SLOT_MINUTES) values.push(minutesToTime(t))
    return values
  }, [])

  const [legislatorId, setLegislatorId] = useState('')
  const [slotTime, setSlotTime] = useState('')
  const [askId, setAskId] = useState('')
  const [room, setRoom] = useState('')
  const [duration, setDuration] = useState(30)
  const [withWhom, setWithWhom] = useState<MeetingWith>('Unknown')

  // (Re)initialize the form whenever a new context opens the modal.
  useEffect(() => {
    if (!context) return
    const leg = legislators.find((l) => l.legislatorId === context.legislatorId)
    setLegislatorId(context.legislatorId)
    setSlotTime(context.slotTime)
    setAskId('')
    setRoom(leg?.officeRoom ?? '')
    setDuration(30)
    setWithWhom('Unknown')
  }, [context, legislators])

  if (!context) return null

  const pick = !!context.pick
  const selectedLegislator = legislators.find((l) => l.legislatorId === legislatorId)

  const handleLegislatorChange = (id: string) => {
    setLegislatorId(id)
    // Re-prefill the room from the newly-selected legislator's office.
    const leg = legislators.find((l) => l.legislatorId === id)
    setRoom(leg?.officeRoom ?? '')
  }

  const handleCreate = () => {
    if (!legislatorId || !slotTime) return
    const ask = askId ? asks.find((a) => a.id === askId) : undefined

    let assignments: ScheduleAssignmentDto[] = []
    if (context.pendingAttendeeId) {
      const attendee = attendees.find((a) => a.attendeeId === context.pendingAttendeeId)
      if (attendee && selectedLegislator) {
        assignments = [{
          assignmentId: `new:${uuid()}`,
          attendeeId: attendee.attendeeId,
          fullName: attendee.fullName,
          constituency: constituencyFor(attendee, selectedLegislator),
          attendance: 'Unmarked',
          role: 'Member',
        }]
      }
    }

    const meeting: ScheduleMeetingDto = {
      id: `new:${uuid()}`,
      legislatorId,
      askId: askId || null,
      askTitle: ask?.title ?? null,
      meetingDate: currentDay,
      startTime: slotTime,
      endTime: minutesToTime(timeToMinutes(slotTime) + duration),
      room: room.trim() || null,
      status: 'Confirmed',
      withWhom,
      packetNotes: null,
      notes: null,
      validity: 'Empty',
      assignments,
      askResponse: 'NotRecorded',
      responseNotes: null,
      attendeeReportCount: 0,
      staff: [],
    }
    onCreate(meeting)
  }

  return (
    <ResponsiveModal isOpen={context !== null} onClose={onClose} isCentered size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>New meeting</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack align="stretch" gap={3}>
              {pick ? (
                <Field.Root>
                  <Field.Label fontSize="sm">Legislator</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={legislatorId}
                      onChange={(e) => handleLegislatorChange(e.target.value)}>
                      {legislators.map((l) => (
                        <option key={l.legislatorId} value={l.legislatorId}>{l.fullName}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              ) : (
                <Text fontSize="sm">
                  <Text as="span" color="gray.500">Legislator: </Text>
                  {selectedLegislator?.fullName ?? '—'}
                </Text>
              )}

              {pick ? (
                <Field.Root>
                  <Field.Label fontSize="sm">Start time</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={slotTime}
                      onChange={(e) => setSlotTime(e.target.value)}>
                      {slotValues.map((v) => (
                        <option key={v} value={v}>{formatSlotLabel(v)}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              ) : (
                <Text fontSize="sm">
                  <Text as="span" color="gray.500">Start: </Text>
                  {formatSlotLabel(slotTime)}
                </Text>
              )}

              <Field.Root>
                <Field.Label fontSize="sm">Ask</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    placeholder="No ask"
                    value={askId}
                    onChange={(e) => setAskId(e.target.value)}>
                    {asks.map((a) => (
                      <option key={a.id} value={a.id}>{a.title}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label fontSize="sm">Room</Field.Label>
                <Input size="sm" value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Office / room" />
              </Field.Root>

              <HStack gap={3} align="flex-end">
                <Field.Root>
                  <Field.Label fontSize="sm">Duration</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">With</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={withWhom}
                      onChange={(e) => setWithWhom(e.target.value as MeetingWith)}>
                      {WITH_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </HStack>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorPalette="brand" onClick={handleCreate} disabled={!legislatorId || !slotTime}>
              Create meeting
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
